import React from 'react';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import { Message } from 'flow/types/Chats';
import { TypedMap } from 'flow/types/TypedMap';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';
import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import useTruncateWithToggle from 'flow/hooks/useExpandableText';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectChatMemberFirstName } from 'flow/selectors/chat-selectors';
import Tooltip from 'flow/components/ui-components/Tooltip';
import Button from 'flow/components/ui-components/Button';

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
    videoDark: boolean;
}

/**
 * The message a reply quotes, shown above the reply's own text: the quoted
 * sender's first name and the quoted text, truncated with a toggle. Renders
 * nothing for a message that quotes none.
 */
const ChatMessageQuote = ({ message, sessionKey, videoDark }: Props) => {
    const { i18next } = useAppContext();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const { text, isExpanded, toggle, isOverflowing } = useTruncateWithToggle(
        message.getIn(['quotedMessage', 'content']),
        { maxLength: 120 },
    );

    const quotedSenderExtensionKey = message.getIn([
        'quotedMessage',
        'sender',
        'extensionKey',
    ]);

    const senderIsYou = quotedSenderExtensionKey === ownExtensionKey;

    // Reads one name out of the members list rather than taking the whole
    // session through useChat: the session is rewritten on every message
    // and on every read receipt, which would re-render every message in
    // the list for a name only a reply displays.
    const quotedSenderFirstName = useAppSelector((state: State) =>
        selectChatMemberFirstName(state, sessionKey, quotedSenderExtensionKey),
    );

    if (!message.getIn(['quotedMessage', 'quotedMessageKey'])) {
        return null;
    }

    // Someone who has left the chat, or the customer, is in no members
    // list at all, and without a fallback the quote renders as a bare
    // ": text".
    const quotedMessageContact = senderIsYou
        ? i18next.t('chat.you')
        : quotedSenderFirstName || i18next.t('Contact.Unknown');

    return (
        <div
            className={twMerge(
                'max-w-[calc(568px_-_44px)] text-sm break-words whitespace-pre-wrap',
                videoDark ? 'text-gray-400' : 'text-gray-500',
            )}
        >
            <Tooltip
                label={`${i18next.t('message.original_message')}: ${moment(
                    message.getIn(['quotedMessage', 'sent']),
                ).format('YYYY-MM-DD HH:mm')}`}
                placement="top-start"
                tooltipClassName="!inline"
            >
                {quotedMessageContact}{' '}
            </Tooltip>
            : {text}
            {isOverflowing && (
                <Button
                    className="override-hover text-link inline !min-w-0 font-bold hover:underline"
                    size="small"
                    onClick={toggle}
                >
                    {isExpanded
                        ? i18next.t('message.input.show_less')
                        : i18next.t('message.input.show_more')}
                </Button>
            )}
        </div>
    );
};

export default ChatMessageQuote;
